import React, { useState, useEffect, useRef } from "react";
import styles from "src/Assets/css/home/PostAttachmentViewer.module.css";
import { Modal, Box, Typography, Popover } from "@mui/material";
import closeIcon from "src/Assets/images/CloseButton.svg";
import attachIcon from "src/Assets/images/attachIcon.png";
import { FaEye } from "react-icons/fa";
import { HiDownload } from "react-icons/hi";
import edit from "src/Assets/images/edit.svg";
import deleteicon from "src/Assets/images/Delete.svg";
import sampledialogcancel from "src/Assets/images/Add.png";
import feedstyles from "src/Assets/css/home/feedpost.module.css";
import API from "../network";
import { useSelector } from "react-redux";
import Cookies from "js-cookie";

const isNonPreviewableDocument = (fileName = "") =>
  /\.(docx?|xlsx?|pptx?|odt|ods|odp|csv|txt|rtf)$/i.test(fileName);

const PostAttachmentViewer = ({
  docLocation,
  userId,
  docTagged,
  docName,
  messageID,
  className = "",
  activeTooltip,
  setActiveTooltip,
  onDeleteAttachment,
}) => {
  const baseUrl = process.env.REACT_APP_SKOPIC_IMAGE_OTHER;
  const userDetails = useSelector((state) => state.SignInReducer.userDetails);

  // --- Local state for attachment URL, tag status, and filename
  const [localDocLocation, setLocalDocLocation] = useState(docLocation);
  const [localDocTagged, setLocalDocTagged] = useState(docTagged);
  const [localFileName, setLocalFileName] = useState(docName || "");
  const [isUpdating, setIsUpdating] = useState(false);
  const [file, setFile] = useState(null);
  const [showPreviewError, setShowPreviewError] = useState(false);
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const isTooltipOpen = activeTooltip === messageID;
  const signInStatus = Cookies.get("signInStatus");

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 400,
    bgcolor: "background.paper",
    borderRadius: 2,
    boxShadow: 24,
    p: 3,
    outline: "none",
    textAlign: "center",
  };

  useEffect(() => {
    if (!isTooltipOpen) {
      setAnchorEl(null);
    }
  }, [isTooltipOpen]);

  useEffect(() => {
    // Update location and tag status normally
    setLocalDocLocation(docLocation);
    setLocalDocTagged(docTagged);

    // Always trust backend filename — clean & reliable
    setLocalFileName(docName || "");

    // If user selects a new file locally, use that file name
    if (file) {
      setLocalFileName(file.name);
    }
  }, [docLocation, docTagged, docName, file]);

  // --- Cleanup temporary URL to avoid memory leaks
  useEffect(() => {
    return () => {
      if (file && localDocLocation?.startsWith("blob:")) {
        URL.revokeObjectURL(localDocLocation);
      }
    };
  }, [file, localDocLocation]);

  // --- Hide component if no attachment
  if (!localDocTagged || !localDocLocation) return null;

  // --- Build full URL

  const fileUrl = localDocLocation?.startsWith("blob:")
    ? localDocLocation
    : `${baseUrl}${localDocLocation}`;

  // 👁 VIEW
  const handleView = () => {
    const newWindow = window.open(fileUrl, "_blank", "noopener,noreferrer");
    // setTimeout(() => {
    //   if (!newWindow || newWindow.closed || newWindow.innerHeight === 0) {
    //     // Likely blocked or invalid file preview
    //     setShowPreviewError(true);
    //   }
    // }, 1500);
    setActiveTooltip(null);
    setAnchorEl(null);
  };

  //  DOWNLOAD (supports both backend URL and blob URL)
  const handleDownload = async () => {
    try {
      // If blob URL (new file uploaded locally)
      if (fileUrl.startsWith("blob:")) {
        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = localFileName || "file";
        document.body.appendChild(link);
        link.click();
        link.remove();
        setActiveTooltip(null);
        setAnchorEl(null);
        return;
      }

      // Backend URL — use fetch
      const downloadUrl = fileUrl.includes("?")
        ? `${fileUrl}&download=true`
        : `${fileUrl}?download=true`;

      const response = await fetch(downloadUrl, { mode: "cors" });
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = localFileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);

      setActiveTooltip(null);
      setAnchorEl(null);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  // UPDATE
  const handleFileChange = async (e) => {
    const newFile = e.target.files[0];
    if (!newFile) return;

    setFile(newFile);
    setIsUpdating(true);

    try {
      const formData = new FormData();
      formData.append("file", newFile);
      formData.append("messageID", messageID);

      const response = await API.updateAttachment(formData);

      if (response?.data?.status === "updated successfully") {
        alert("Attachment updated successfully!");

        // Use temporary URL for immediate UI update
        const tempUrl = URL.createObjectURL(newFile);
        setLocalDocLocation(tempUrl);
        setLocalDocTagged(1);
      }
    } catch (error) {
      console.error("Update failed:", error);
      alert("Failed to update attachment.");
    } finally {
      setIsUpdating(false);
      setActiveTooltip(null);
    }
  };

  // DELETE
  const handleDelete = () => {
    if (onDeleteAttachment) {
      onDeleteAttachment(messageID);
    }
    setLocalDocTagged(0);
    setLocalDocLocation(null);
    setLocalFileName("");
    setFile(null);
  };

  const handleCloseTooltip = () => {
    setActiveTooltip(null);
    setAnchorEl(null);
  };

  return (
    <span
      className={`${styles.fileName} ${className || ""}`}
      style={{
        position: "relative",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
      }}
      onClick={(e) => {
        if (isTooltipOpen) {
          handleCloseTooltip();
        } else {
          setAnchorEl(e.currentTarget);
          setActiveTooltip(messageID);
        }
      }}
    >
      {localFileName}

      <Popover
        open={Boolean(isTooltipOpen && anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseTooltip}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.15)",
            borderRadius: "10px",
            border: "1px solid #ccc",
            overflow: "visible",
            mt: "6px",
          },
        }}
        sx={{
          zIndex: 3500,
        }}
      >
        {signInStatus === "true" ? (
          <div className={styles.tooltip}>
            <button
              className={styles.tooltipClose}
              onClick={(e) => {
                e.stopPropagation();
                handleCloseTooltip();
              }}
            >
              <img src={closeIcon} alt="closeIcon" />
            </button>
            {!isNonPreviewableDocument(localFileName) && (
              <button onClick={handleView}>
                <FaEye className={styles.fileViewIcon} />{" "}
                <span className={styles.fileView}>View</span>
              </button>
            )}
            <button onClick={handleDownload}>
              <HiDownload className={styles.fileViewIcon} />{" "}
              <span className={styles.fileView}>Download</span>
            </button>
          </div>
        ) : (
          <div
            className={styles.tooltip}
            style={{ width: "300px" }}
          >
            <button
              className={styles.tooltipClose}
              onClick={(e) => {
                e.stopPropagation();
                handleCloseTooltip();
              }}
            >
              <img src={closeIcon} alt="closeIcon" />
            </button>
            <p className={styles.textName}>
              Please sign in to view this attachment.
            </p>
          </div>
        )}
      </Popover>

      <Modal
        open={showPreviewError}
        onClose={() => setShowPreviewError(false)}
        aria-labelledby="preview-error-title"
        aria-describedby="preview-error-description"
      >
        <Box sx={style}>
          <Typography
            id="preview-error-title"
            variant="h6"
            sx={{ mb: 1, fontWeight: 600, color: "#373334" }}
          >
            File Preview
          </Typography>

          <Typography
            id="preview-error-description"
            sx={{ mb: 3, color: "text.secondary" }}
          >
            Preview may not be available for certain file types. If the file
            doesn’t open properly in the new tab, you can download it to view.
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
            <button
              onClick={() => {
                handleDownload();
                setShowPreviewError(false);
              }}
              style={{
                borderRadius: "10px",
                padding: "5px 18px",
                outline: "none !important",
                backgroundColor: "#1976d2",
                color: "#fff",
                textTransform: "none",
                fontWeight: 500,
                border: "none",
              }}
            >
              Download
            </button>
            <button
              onClick={() => setShowPreviewError(false)}
              style={{
                borderRadius: "12px",
                padding: "5px 20px",
                border: "1px solid #ccc",
                backgroundColor: "#f8f9fa",
                color: "#555",
                fontWeight: 500,
                cursor: "pointer",
                textTransform: "none",
                marginLeft: "10px",
                outline: "none !important",
              }}
            >
              Close
            </button>
          </Box>
        </Box>
      </Modal>
      <Modal
        onClose={() => setDeleteConfirmationModal(false)}
        open={deleteConfirmationModal}
        aria-labelledby="delete-confirmation-title"
        aria-describedby="delete-confirmation-description"
        sx={{
          zIndex: 3500,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "white",
            padding: "20px",
            borderRadius: "8px",
            outline: "none",
            boxShadow: 24,
            minWidth: 300,
          }}
        >
          <div className={feedstyles.sampledialogcountandcancel}>
            <p>
              <b>Delete Confirmation</b>
            </p>
            <a onClick={() => setDeleteConfirmationModal(false)}>
              <img src={sampledialogcancel} alt="" />
            </a>
          </div>
          <div>
            <p>Are you sure you want to delete this Attachment?</p>
            <button
              onClick={handleDelete}
              className={feedstyles.dialogdeletebutton}
            >
              Delete
            </button>
          </div>
        </Box>
      </Modal>
    </span>
  );
};

export default PostAttachmentViewer;
